import React, { useState, useEffect, createContext, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { AppointmentDetailsResponse } from '../../types';
import { REVIEW_STEPS } from '../../routes';
import reviewService from '../../services/reviewService';
import apruvRateService from '../../services/apruvRateService';
import AddReview from '../pages/AddReview';
import NegativeReview from '../pages/NegativeReview';
import Tipping from '../pages/Tipping';
import PaymentMethod from '../pages/PaymentMethod';
import ThankYou from '../pages/ThankYou';
import ReviewDone from '../pages/ReviewDone';
import TipTech from '../pages/TipTech';
import PoweredByApruv from '../common/PoweredByApruv';
import PublicCXSurveyWrapper from './PublicCXSurveyWrapper';

const isOpaqueCXSurveyCode = (value?: string) => Boolean(value && !/^\d+$/.test(value));

// Context for providing navigation and params to child components
interface ReviewContextType {
  companyName: string;
  appointmentId: string;
  currentStep: string;
  appointmentDetails: AppointmentDetailsResponse;
  navigate: (urlOrStep: string, options?: { state?: any }) => void;
  // Direct step navigation functions
  goToAdd: () => void;
  goToNegative: () => void;
  goToTipping: () => void;
  goToPayment: () => void;
  goToThankYou: () => void;
  goToDone: () => void;
  goToTipTech: () => void;
}

const ReviewContext = createContext<ReviewContextType | null>(null);

// Custom hook to use the review context
export const useReviewContext = () => {
  const context = useContext(ReviewContext);
  if (!context) {
    throw new Error('useReviewContext must be used within ReviewWrapper');
  }
  return context;
};

// Custom hook that mimics useParams but returns the context values
export const useReviewParams = () => {
  const context = useContext(ReviewContext);
  const routerParams = useParams();
  
  if (context) {
    return {
      companyName: context.companyName,
      appointmentId: context.appointmentId
    };
  }
  
  // Fallback to regular useParams if not in ReviewWrapper context
  return routerParams;
};

// Custom hook that provides navigation with step management via history.state
export const useReviewNavigate = () => {
  const context = useContext(ReviewContext);
  const routerNavigate = useNavigate();
  
  if (context) {
    return context.navigate;
  }
  
  // Fallback to regular useNavigate if not in ReviewWrapper context
  return routerNavigate;
};

// Custom hook to access appointment details from context
export const useAppointmentDetails = () => {
  const context = useContext(ReviewContext);
  if (context) {
    return context.appointmentDetails;
  }
  
  // Return null if not in ReviewWrapper context
  return null;
};

// Custom hook for step navigation
export const useStepNavigation = () => {
  const context = useContext(ReviewContext);
  if (context) {
    return {
      goToAdd: context.goToAdd,
      goToNegative: context.goToNegative,
      goToTipping: context.goToTipping,
      goToPayment: context.goToPayment,
      goToThankYou: context.goToThankYou,
      goToDone: context.goToDone,
      goToTipTech: context.goToTipTech
    };
  }
  
  // Return empty functions if not in ReviewWrapper context
  return {
    goToAdd: () => { /* intentionally empty: fallback navigation */ },
    goToNegative: () => { /* intentionally empty: fallback navigation */ },
    goToTipping: () => { /* intentionally empty: fallback navigation */ },
    goToPayment: () => { /* intentionally empty: fallback navigation */ },
    goToThankYou: () => { /* intentionally empty: fallback navigation */ },
    goToDone: () => { /* intentionally empty: fallback navigation */ },
    goToTipTech: () => { /* intentionally empty: fallback navigation */ }
  };
};

/**
 * ReviewWrapper component that handles the review workflow with ultra-simplified URLs
 * 
 * This component:
 * - Takes only a code parameter from the URL (/:code)
 * - Manages current step via browser history.state
 * - Fetches appointment details using the code
 * - Renders the appropriate review step component based on history.state
 */
const LegacyReviewWrapper: React.FC<{ code?: string }> = ({ code }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [appointmentDetails, setAppointmentDetails] = useState<AppointmentDetailsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<string>(REVIEW_STEPS.ADD);

  // Get current step from history.state or default to 'add'
  useEffect(() => {
    const stateStep = location.state?.step;
    if (stateStep && Object.values(REVIEW_STEPS).includes(stateStep)) {
      setCurrentStep(stateStep);
    } else {
      setCurrentStep(REVIEW_STEPS.ADD);
    }
    
    // Store any additional state data for components to access
    if (location.state && Object.keys(location.state).length > 1) {
      // Additional state is available for components
      console.log('Additional state:', location.state);
    }
  }, [location.state]);

  // Fetch appointment details when component mounts or code changes
  useEffect(() => {
    if (!code) {
      setError('No appointment code provided');
      setLoading(false);
      return;
    }

    const fetchAppointmentDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await reviewService.getAppointmentDetails(code);
        setAppointmentDetails(response);

        // Also fetch rate configuration if we have company and location IDs
        if (response.appointment_details.CompanyID && response.appointment_details.LocationID) {
          try {
            await apruvRateService.getConfig(
              response.appointment_details.CompanyID,
              response.appointment_details.LocationID
            );
          } catch (configError) {
            console.warn('Failed to fetch rate config:', configError);
            // Don't fail the whole component if rate config fails
          }
        }
      } catch (err) {
        console.error('Error fetching appointment details:', err);
        setError('Failed to load appointment details. Please check the appointment code.');
      } finally {
        setLoading(false);
      }
    };

    fetchAppointmentDetails();
  }, [code]);

  // Create navigation function that manages steps via history.state
  const navigateToStep = (step: string, additionalState?: any) => {
    if (!code) return;
    
    // Always navigate to the same URL but with different state
    navigate(`/${code}`, {
      state: { step, ...additionalState },
      replace: false // Create new history entry so back button works
    });
  };

  // Create navigation function that transforms old-style URLs to step navigation
  const createNavigateFunction = (companyName: string, appointmentId: string) => {
    return (url: string, options?: { state?: any }) => {
      if (!code) return;
      
      // Transform old-style URLs to step navigation
      if (url.includes(`/review/negative/${companyName}/${appointmentId}`)) {
        navigateToStep(REVIEW_STEPS.NEGATIVE, options?.state);
      } else if (url.includes(`/review/tip/${companyName}/${appointmentId}`)) {
        navigateToStep(REVIEW_STEPS.TIPPING, options?.state);
      } else if (url.includes(`/review/payment/${companyName}/${appointmentId}`)) {
        navigateToStep(REVIEW_STEPS.PAYMENT, options?.state);
      } else if (url.includes(`/review/thank-you/${companyName}/${appointmentId}`)) {
        navigateToStep(REVIEW_STEPS.THANK_YOU, options?.state);
      } else if (url.includes(`/review/done/${companyName}/${appointmentId}`)) {
        navigateToStep(REVIEW_STEPS.DONE, options?.state);
      } else if (url.includes(`/review/tip-tech/${companyName}/${appointmentId}`)) {
        navigateToStep(REVIEW_STEPS.TIP_TECH, options?.state);
      } else if (url.includes(`/review/add/${companyName}/${appointmentId}`)) {
        navigateToStep(REVIEW_STEPS.ADD, options?.state);
      } else {
        // For any other URLs, pass them through as-is
        navigate(url, options);
      }
    };
  };

  // Determine which component to render based on the current step
  const renderStepComponent = () => {
    if (!appointmentDetails) return null;

    const { CompanyName, AppointmentId } = appointmentDetails.appointment_details;
    
    // Create context value
    const contextValue: ReviewContextType = {
      companyName: CompanyName,
      appointmentId: AppointmentId.toString(),
      currentStep,
      appointmentDetails,
      navigate: createNavigateFunction(CompanyName, AppointmentId.toString()),
      // Direct step navigation functions
      goToAdd: () => navigateToStep(REVIEW_STEPS.ADD),
      goToNegative: () => navigateToStep(REVIEW_STEPS.NEGATIVE),
      goToTipping: () => navigateToStep(REVIEW_STEPS.TIPPING),
      goToPayment: () => navigateToStep(REVIEW_STEPS.PAYMENT),
      goToThankYou: () => navigateToStep(REVIEW_STEPS.THANK_YOU),
      goToDone: () => navigateToStep(REVIEW_STEPS.DONE),
      goToTipTech: () => navigateToStep(REVIEW_STEPS.TIP_TECH)
    };

    let ComponentToRender;
    
    switch (currentStep) {
      case REVIEW_STEPS.NEGATIVE:
        ComponentToRender = NegativeReview;
        break;
      case REVIEW_STEPS.TIPPING:
        ComponentToRender = Tipping;
        break;
      case REVIEW_STEPS.PAYMENT:
        ComponentToRender = PaymentMethod;
        break;
      case REVIEW_STEPS.THANK_YOU:
        ComponentToRender = ThankYou;
        break;
      case REVIEW_STEPS.DONE:
        ComponentToRender = ReviewDone;
        break;
      case REVIEW_STEPS.TIP_TECH:
        ComponentToRender = TipTech;
        break;
      default:
        // Default to AddReview for unknown steps or 'add'
        ComponentToRender = AddReview;
        break;
    }

    return (
      <ReviewContext.Provider value={contextValue}>
        <ComponentToRender />
      </ReviewContext.Provider>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div
        className="flex align-items-center justify-content-center px-3"
        style={{
          flexDirection: 'column',
          gap: '1rem',
          minHeight: '100vh',
          paddingLeft: '1.5rem',
          paddingRight: '1.5rem',
        }}
      >
        <i className="pi pi-spin pi-spinner" style={{ fontSize: '4rem' }}></i>
        <PoweredByApruv />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div
        className="flex align-items-center justify-content-center px-3"
        style={{
          flexDirection: 'column',
          gap: '1rem',
          minHeight: '100vh',
          paddingLeft: '1.5rem',
          paddingRight: '1.5rem',
        }}
      >
        <div style={{ textAlign: 'center', maxWidth: '400px', padding: '2rem' }}>
          <i className="pi pi-exclamation-triangle" style={{ fontSize: '4rem', color: '#ef4444', marginBottom: '1.5rem' }}></i>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#374151', marginBottom: '1rem' }}>
            Something went wrong
          </h2>
          <p style={{ fontSize: '1rem', color: '#6b7280', lineHeight: '1.5' }}>
            {error}
          </p>
        </div>
        <PoweredByApruv />
      </div>
    );
  }

  // Render the appropriate step component
  return (
    <div className="px-3" style={{ paddingLeft: '1.5rem', paddingRight: '1.5rem' }}>
      {renderStepComponent()}
    </div>
  );
};

const ReviewWrapper: React.FC = () => {
  const { code } = useParams<{ code: string }>();

  if (isOpaqueCXSurveyCode(code)) {
    return <PublicCXSurveyWrapper surveyCode={code} />;
  }

  return <LegacyReviewWrapper code={code} />;
};

export default ReviewWrapper;
